/* eslint-disable sort-keys-fix/sort-keys-fix */

import Logo from './Logo';

export const baseColors = {
  black: '#000',
  white: '#fff',
  gray: [
    '#fff', // 0 index
    '#f8f8f8',
    '#dedede',
    '#979797',
    '#3d3d3d', // 4
  ],
  primary: '#172983',
  secondary: '#97BE0D',
  secondaryHover: '#83A237',
  tertiary: '#F0F3F9',
  yellow: '#E9B84F',
  red: '#f00101',
};

export const colors = {
  ...baseColors,
  background: baseColors.white,
  danger: baseColors.red,
  dark: baseColors.gray[4],
  darkGray: baseColors.gray[4],
  info: baseColors.primary,
  light: baseColors.gray[2],
  lightGray: baseColors.gray[2],
  muted: baseColors.gray[1],
  primaryWhite: baseColors.white,
  success: baseColors.secondary,
  text: baseColors.gray[4],
  textMuted: baseColors.gray[3],
  warning: baseColors.yellow,
};

export const space = [0, 0.25, 0.5, 1, 1.5, 3].map(n => `${n}rem`);

export const breakpoints = ['576px', '768px', '992px'];

export const fonts = {
  body: 'Open Sans, Arial, sans-serif',
  heading: 'inherit',
  monospace: 'Menlo, monospace',
};

export const fontWeights = {
  body: 400,
  bold: 700,
  display: 300,
  heading: 700,
  headingAlt: 400,
  light: 300,
  normal: 400,
};

export const fontSizes = {
  0: '0.875rem',
  1: '1',
  2: '1.125rem',
  3: '1.25rem',
  4: '1.625rem',
  5: '1.875rem',
  6: '2rem',
  7: '2.1875rem',
  8: '2.25rem',
  9: '2.5rem',
  10: '2.8125rem',
  11: '3.4375rem',
};

export const lineHeights = {
  body: 1.5,
  heading: 1.5,
  paragraph: '1.75rem',
};

export const sizes = {
  lg: '1150px',

  md: '720px',
  // container widths
  sm: '540px',
  xl: '1440px',
};

export const radii = {
  default: '0.25rem',
  lg: '0.3rem',
  pill: '50rem',
  sm: '0.2rem',
};

export const shadows = {
  default: '0 .5rem 1rem rgba(0, 0, 0, .15)',
  lg: '0 1rem 3rem rgba(0, 0, 0, .175)',
  sm: '0 .125rem .25rem rgba(0, 0, 0, .075)',
};

export const text = {
  caps: {
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
  },
  heading: {
    fontFamily: fonts.heading,
    fontWeight: fontWeights.heading,
    lineHeight: lineHeights.heading,
    textTransform: 'uppercase',
    color: colors.primary,
  },
};

const heading = {
  fontFamily: fonts.heading,
  fontWeight: fontWeights.heading,
  lineHeight: lineHeights.heading,
  color: text.heading.color,
};

const display = {
  fontWeight: fontWeights.display,
  lineHeight: lineHeights.heading,
};

// variants
const typeStyles = {
  display,
  heading,
};

export const styles = {
  a: {
    '&:hover': {
      color: colors.secondaryHover,
    },
    fontWeight: 600,
    color: colors.primary,
    textDecoration: 'underline',
  },
  blockquote: {
    backgroundColor: colors.tertiary,
    color: colors.primary,
    fontWeight: 'normal',
    textTransform: 'normal',
  },
  h1: {
    ...heading,
    fontSize: { desktop: fontSizes[11], tablet: fontSizes[10], mobile: fontSizes[8] },
    fontWeight: 700,
    lineHeight: { desktop: '4.0625rem', tablet: '3.125rem', mobile: '2.5rem' },
  },
  h2: {
    ...heading,
    fontSize: { desktop: fontSizes[9], tablet: fontSizes[7], mobile: fontSizes[6] },
    fontWeight: 400,
    lineHeight: { desktop: '2.6875rem', tablet: '2.375rem', mobile: '2.25rem' },
  },
  h3: {
    ...heading,
    fontSize: { desktop: fontSizes[5], tablet: fontSizes[4], mobile: fontSizes[3] },
    fontWeight: 700,
    lineHeight: { desktop: '2.1875rem', tablet: '1.875rem', mobile: '1.625rem' },
  },
  h4: {
    ...heading,
    fontSize: { desktop: fontSizes[3], tablet: fontSizes[2], mobile: fontSizes[2] },
    fontWeight: 700,
    lineHeight: { desktop: '1.5rem', tablet: '1.375rem', mobile: '1.375rem' },
  },
  h5: {
    ...heading,
    fontSize: { desktop: fontSizes[1], tablet: fontSizes[1], mobile: fontSizes[1] },
    fontWeight: 400,
    lineHeight: { desktop: '1.25rem', tablet: '1.25rem', mobile: '1.25rem' },
  },
  highlight: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  img: {
    height: 'auto',
    maxWidth: '100%',
  },
  inlineCode: {
    color: colors.darkGray,
  },
  li: {
    bulletBorderRadius: '10px',
    bulletColor: colors.primary,
  },
  p: {
    fontSize: fontSizes[2],
    lineHeight: lineHeights.paragraph,
    margin: '0',
    marginBottom: '1.7rem',
  },
  root: {
    backgroundColor: colors.white,
    fontFamily: fonts.body,
    fontWeight: fontWeights.body,
    lineHeight: lineHeights.body,
  },
  rteHighlight: {
    hoverColor: colors.secondaryHover,
    color: colors.primary,
    fontWeight: 600,
    textDecoration: 'underline',
    isFooter: colors.white,
  },
  table: {
    borderCollapse: 'collapse',
    color: colors.gray[3],
    marginBottom: '3rem',
    width: '100%',
  },
  td: {
    borderBottomColor: colors.gray[3],
    borderBottomStyle: 'solid',
    borderBottomWidth: 2,
    padding: '.75rem',
    verticalAlign: 'top',
  },
  th: {
    borderBottomColor: colors.gray[3],
    borderBottomStyle: 'solid',
    borderBottomWidth: '2px',
    borderTopColor: colors.gray[3],
    borderTopStyle: 'solid',
    borderTopWidth: '2px',
    padding: '.75rem',
    textAlign: 'inherit',
    verticalAlign: 'bottom',
  },
};

const buttonDefaults = {
  borderRadius: '10px',
  cursor: 'pointer',
  fontFamily: fonts.body,
  fontSize: fontSizes[2],
  fontWeight: 600,
  height: 'auto',
  padding: '15px 25px',
  lineHeight: 1,
  transition:
    'background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1),border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
};

export const buttons = {
  disabled: {
    ...buttonDefaults,
    backgroundColor: colors.muted,
    color: colors.textMuted,
    cursor: 'default',
  },
  primary: {
    ...buttonDefaults,
    '&:hover': {
      backgroundColor: colors.secondaryHover,
      color: colors.primary,
    },
    backgroundColor: colors.secondary,
    borderColor: colors.secondary,
    borderStyle: 'solid',
    borderWidth: '1px',
    color: colors.primary,
  },
  'primary-with-background': {
    ...buttonDefaults,
    '&:hover': {
      backgroundColor: colors.white,
      borderColor: colors.white,
      borderWidth: '1px',
      color: colors.secondaryHover,
    },
    backgroundColor: colors.white,
    borderColor: colors.white,
    borderStyle: 'solid',
    borderWidth: '1px',
    color: colors.primary,
  },
  secondary: {
    ...buttonDefaults,
    '&:hover': {
      backgroundColor: colors.primary,
      color: colors.white,
      borderColor: colors.primary,
    },
    backgroundColor: colors.white,
    borderColor: colors.white,
    borderStyle: 'solid',
    borderWidth: '1px',
    color: colors.primary,
  },
  tertiary: {
    ...buttonDefaults,
    '&:hover': {
      backgroundColor: colors.primary,
      color: colors.white,
    },
    backgroundColor: colors.white,
    borderColor: colors.primary,
    borderStyle: 'solid',
    borderWidth: '1px',
    color: colors.primary,
  },
};

// COMPONENTS
export const adviceCardModule = {
  backgroundColor: colors.primary,
};

export const articleDetail = {
  introduction: colors.text,
  tags: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  title: colors.text,
};

export const articleListing = {
  background: colors.primary,
  filter: colors.text,
  hover: colors.secondary,
  mobile: {
    title: colors.primary,
    text: colors.text,
    background: colors.tertiary,
  },
  text: colors.white,
  title: colors.text,
};

export const breadcrumb = {
  backgroundColor: colors.tertiary,
};

export const cardCtas = {
  arrowColor: colors.primary,
  arrowHoverColor: colors.secondary,
  color: colors.primary,
  fontSize: '1rem',
  hoverColor: colors.secondaryHover,
  letterSpacing: '0px',
  lineHeight: '13px',
};

export const cards = {
  article: {
    description: colors.text,
    link: {
      '&:hover': {
        color: colors.secondaryHover,
        textDecoration: 'underline',
        opacity: 1,
      },
      color: colors.primary,
    },
    title: colors.primary,
  },
  logo: {
    color: colors.text,
  },
};

export const carousel = {
  controls: {
    regular: {
      backgroundColor: colors.primary,
      color: colors.white,
      hover: {
        backgroundColor: colors.primary,
        color: colors.white,
      },
    },
    hero: {
      backgroundColor: colors.primary,
      color: colors.white,
      stroke: colors.primary,
      hover: {
        backgroundColor: colors.white,
        color: colors.primary,
        stroke: colors.white,
      },
    },
  },
  indicator: colors.primary,
  link: {
    '&:hover': {
      color: colors.secondaryHover,
      opacity: 1,
      textDecoration: 'underline',
    },
    color: colors.primary,
  },
  text: colors.text,
};

export const categoryCards = {
  backgroundColor: colors.secondary,
};

export const categorySection = {
  background: colors.primary,
  boxShadow: null,
  chevron: {
    fill: colors.white,
    stroke: colors.primary,
  },
  color: colors.primaryWhite,
  date: colors.primary,
  dateColor: colors.primaryWhite,
  headingColor: colors.dark,
};

export const columns = {
  imageCard: colors.text,
  imageCardTitle: colors.text,
  infoBlock: {
    background: colors.primary,
    description: colors.white,
    title: colors.white,
  },
};

export const contactForm = {
  heading: {
    color: colors.primary,
  },
};

export const contacts = {
  office: {
    mapTextColor: colors.primary,
    mapTextFontSize: '0.875rem',
    mapTextFontWeight: 'normal',
    mapTextLetterSpacing: '2.33px',
    mapTextLineHeight: '1.71',
    mapTextTransform: 'uppercase',
    openingHoursFontSize: '1rem',
  },
};

export const cookieBanner = {
  backgroundColor: colors.primary,
};

export const ctaBanner = {
  focusImage: {
    backgroundColor: colors.primary,
    color: colors.white,
    titleColor: colors.white,
  },
  focusText: {
    backgroundColor: colors.primary,
    color: colors.white,
    titleColor: colors.white,
  },
};

export const ctaGroup = {
  title: colors.text,
  borderRadius: 0,
  chevron: {
    opacity: 1,
  },
};

export const customBlockCards = {
  backgroundColor: colors.secondary,
  color: colors.primary,
};

export const documentDownload = {
  arrowColor: colors.primary,
  arrowHoverColor: colors.secondaryHover,
  color: colors.primary,
  hoverColor: colors.secondaryHover,
};
export const footer = {
  activeColor: colors.secondary,
  backgroundColor: colors.primary,
  color: colors.white,
  minHeight: '160px',
};

export const formInputs = {
  activeColor: colors.primary,
  borderRadius: '8px',
  radioInputBorderRadius: '8px',
  radioSpanBorderRadius: '6px',
};

export const header = {
  activeColor: colors.primary,
  backgroundColor: colors.white,
  color: colors.text,
};

export const headerBottom = {
  activeColor: colors.primary,
  activeFontWeight: '600',
  backgroundColor: colors.secondary,
  color: colors.primary,
  fontSize: '0.875rem',
  fontWeight: 'normal',
};

export const heroBannerButton = {
  'No Background': {
    '&:hover': {
      backgroundColor: colors.white,
      borderColor: colors.white,
      color: colors.secondary,
    },
    backgroundColor: 'transparent',
    borderColor: colors.white,
    borderRadius: '10px',
    borderStyle: 'solid',
    borderWidth: '2px',
    color: colors.white,
    height: '42px',
    justifyContent: 'center',
  },
  'White Background': {
    '&:hover': {
      backgroundColor: colors.secondaryHover,
      border: 'none',
      color: colors.primary,
    },
    backgroundColor: colors.secondary,
    borderRadius: '10px',
    color: colors.primary,
    justifyContent: 'center',
    minHeight: '42px',
    padding: '13px 35px 12px',
  },
  arrow: {
    display: 'none',
  },
};

export const heroSection = {
  titleFontWeight: 'normal',
};

export const imageAndText = {
  backgroundColor: colors.primary,
  color: colors.white,
};

export const infoBlock = {
  background: colors.primary,
  button: {
    '&:hover': {
      backgroundColor: colors.white,
      color: colors.secondaryHover,
      textDecoration: 'none',
    },
    backgroundColor: colors.white,
    color: colors.primary,
  },
  description: colors.white,
  title: colors.white,
};

export const infoSection = {
  backgroundColor: colors.secondary,
  color: colors.text,
};

export const ingredients = {
  backgroundColor: colors.primary,
  color: colors.white,
  hoverColor: colors.secondary,
};

export const media = {
  titleFontWeight: 'normal',
  titleTextTransform: 'normal',
};

export const mobileNavigation = {
  arrowColor: colors.secondary,
  chevronColor: colors.darkGray,
};

export const navItems = {
  fontSize: '1rem',
  letterSpacing: '0px',
};

export const newsListing = {
  titleFontWeight: 'normal',
};

export const product = {
  backgroundColor: colors.secondary,
  color: colors.white,
};

export const productListing = {
  titleFontWeight: 'normal',
};

export const salesPerson = {
  borderColor: colors.primary,
};

export const search = {
  activeColor: colors.primary,
  borderRadius: '30px',
  fontSize: '1rem',
  fontWeight: 400,
  textTransform: 'capitalize',
};

export const shortBanner = {
  backgroundColor: colors.primary,
  color: colors.white,
  titleFontWeight: 600,
  titleTextTransform: 'none',
  titleColor: colors.white,
};

export const upComingEvents = {
  backgroundColor: colors.primary,
  color: colors.white,
};

export const dealer = {
  textColor: colors.primary,
};

export const table = {
  headerBackgroundColor: colors.primary,
  headerColor: colors.white,
  scrollbarColor: colors.secondary,
};

export default {
  adviceCardModule,
  articleDetail,
  articleListing,
  breadcrumb,
  buttons,
  cardCtas,
  cards,
  carousel,
  categoryCards,
  categorySection,
  colors,
  columns,
  contactForm,
  contacts,
  cookieBanner,
  ctaBanner,
  ctaGroup,
  customBlockCards,
  dealer,
  documentDownload,
  fonts,
  fontSizes,
  fontWeights,
  footer,
  formInputs,
  header,
  headerBottom,
  heroBannerButton,
  heroSection,
  imageAndText,
  infoBlock,
  infoSection,
  ingredients,
  lineHeights,
  logo: Logo,
  media,
  mobileNavigation,
  name: 'Palital',
  navItems,
  newsListing,
  product,
  productListing,
  radii,
  salesPerson,
  search,
  shadows,
  shortBanner,
  sizes,
  styles,
  table,
  text,
  typeStyles,
  upComingEvents,
};
